import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import {
  ChevronRight,
  GraduationCap,
  MapPin,
  School,
  Users,
} from "lucide-react";
import {
  getTeacherAccountsBySchools,
  getTeacherAccountTeachingLevelStats,
} from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import { showErrorToast } from "../../../components/ConfirmDialog";
import Pagination from "../../../components/common/Pagination";
import TeacherAccountFilter from "./TeacherAccountFilter";
import TeacherAccountFormModal from "./TeacherAccountFormModal";
import TeacherAccountImportModal from "./TeacherAccountImportModal";
import TeacherAccountMiniDashboard from "./TeacherAccountMiniDashboard";
import TeachersBySchoolModal from "./TeachersBySchoolModal";

const TeacherAccountCardPage = () => {
  const [schools, setSchools] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    school_count: 0,
    mam_non: 0,
    tieu_hoc: 0,
    thcs: 0,
    thpt: 0,
    chua_phan_loai: 0,
  });
  const [filters, setFilters] = useState({
    keyword: "",
    branch_id: "",
    customer_id: "",
    status: "",
    school_level: "",
  });
  const [debouncedKeyword] = useDebounce(filters.keyword, 400);
  const [branches, setBranches] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [teacherListVersion, setTeacherListVersion] = useState(0);

  const loadBranches = useCallback(async () => {
    try {
      const b = await getAllBranches_Array();
      setBranches(Array.isArray(b) ? b : []);
    } catch {
      setBranches([]);
    }
  }, []);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    if (!filters.branch_id) {
      setCustomerOptions([]);
      return;
    }
    (async () => {
      try {
        const res = await getCustomersByBranch(filters.branch_id);
        const list = res?.items || res || [];
        setCustomerOptions(Array.isArray(list) ? list : []);
      } catch {
        setCustomerOptions([]);
      }
    })();
  }, [filters.branch_id]);

  const handleFiltersChange = useCallback((next) => {
    setFilters((prev) => {
      if (
        next.branch_id !== prev.branch_id ||
        next.customer_id !== prev.customer_id ||
        next.status !== prev.status
      ) {
        setCurrentPage(1);
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    setCurrentPage(1);
  }, [debouncedKeyword, filters.school_level]);

  const baseFilterParams = useCallback(
    () => ({
      keyword: debouncedKeyword || undefined,
      branch_id: filters.branch_id || undefined,
      customer_id: filters.customer_id || undefined,
      status: filters.status || undefined,
      school_level: filters.school_level || undefined,
    }),
    [
      debouncedKeyword,
      filters.branch_id,
      filters.customer_id,
      filters.status,
      filters.school_level,
    ]
  );

  const fetchStats = useCallback(async () => {
    try {
      const statsRes = await getTeacherAccountTeachingLevelStats({
        keyword: debouncedKeyword || undefined,
        branch_id: filters.branch_id || undefined,
        customer_id: filters.customer_id || undefined,
        status: filters.status || undefined,
      });
      setStats({
        total: Number(statsRes?.total || 0),
        school_count: Number(statsRes?.school_count || 0),
        mam_non: Number(statsRes?.mam_non || 0),
        tieu_hoc: Number(statsRes?.tieu_hoc || 0),
        thcs: Number(statsRes?.thcs || 0),
        thpt: Number(statsRes?.thpt || 0),
        chua_phan_loai: Number(statsRes?.chua_phan_loai || 0),
      });
    } catch {
      setStats({
        total: 0,
        school_count: 0,
        mam_non: 0,
        tieu_hoc: 0,
        thcs: 0,
        thpt: 0,
        chua_phan_loai: 0,
      });
    }
  }, [
    debouncedKeyword,
    filters.branch_id,
    filters.customer_id,
    filters.status,
  ]);

  const fetchSchools = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getTeacherAccountsBySchools({
        ...baseFilterParams(),
        page: currentPage,
        pageSize,
      });
      setSchools(Array.isArray(res?.items) ? res.items : []);
      setTotalRecords(Number(res?.total ?? 0));
    } catch (e) {
      console.error(e);
      showErrorToast("Không tải được danh sách trường.");
      setSchools([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [baseFilterParams, currentPage, pageSize]);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchStats(), fetchSchools()]);
  }, [fetchStats, fetchSchools]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  const handleSelectLevel = (schoolLevel) => {
    setFilters((prev) => ({ ...prev, school_level: schoolLevel }));
    setSelectedSchool(null);
    setCurrentPage(1);
  };

  const schoolLevelBadge = (level) => {
    if (!level) {
      return (
        <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
          Chưa phân loại
        </span>
      );
    }
    return (
      <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide text-slate-700">
        {level}
      </span>
    );
  };

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
        <div data-tour="teacher-accounts-header">
          <div className="mb-1 flex items-center gap-3 sm:mb-2 sm:gap-4">
            <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm sm:rounded-[1.5rem] sm:p-4">
              <GraduationCap className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl font-black uppercase tracking-tight leading-tight text-gray-800 sm:text-2xl">
                Tài khoản <span className="text-red-700">giáo viên đối tác</span>
              </h1>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400 sm:mt-1 sm:tracking-[0.2em]">
                Quản lý theo đơn vị (trường học) — tài khoản gắn khách hàng CRM
              </p>
            </div>
          </div>
        </div>
      </div>

      <TeacherAccountMiniDashboard
        stats={stats}
        selectedLevel={filters.school_level}
        onSelectLevel={handleSelectLevel}
        loading={loading}
      />

      <TeacherAccountFilter
        filters={filters}
        onChange={handleFiltersChange}
        branchOptions={branches}
        customerOptions={customerOptions}
        onNewClick={() => {
          setEditId(null);
          setShowForm(true);
        }}
        onImportClick={() => setShowImport(true)}
        onRefresh={refreshAll}
      />

      <div className="mt-2 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        <div className="overflow-auto">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="bg-gray-50/90">
              <tr className="text-left text-gray-500">
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                  Trường / đơn vị
                </th>
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                  Cấp học
                </th>
                <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wide">
                  Chi nhánh
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                  Giáo viên
                </th>
                <th className="px-4 py-3 text-center text-[11px] font-bold uppercase tracking-wide">
                  Đang hoạt động
                </th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {schools.map((school) => (
                <tr
                  key={school.partner_customer_id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedSchool(school)}
                  onKeyDown={(e) =>
                    e.key === "Enter" && setSelectedSchool(school)
                  }
                  className="cursor-pointer border-t border-gray-100 transition-colors hover:bg-red-50/40"
                >
                  <td className="px-4 py-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-700">
                        <School className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-gray-900">
                          {school.partner_school_name}
                        </p>
                        {school.ward ? (
                          <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-500">
                            <MapPin className="h-3 w-3 shrink-0" />
                            {school.ward}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">{schoolLevelBadge(school.school_level)}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {school.branch_name || "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center gap-1.5 text-sm font-bold text-gray-800">
                      <Users className="h-4 w-4 text-gray-400" />
                      {Number(school.teacher_count || 0)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="font-bold text-emerald-700">
                      {Number(school.active_count || 0)}
                    </span>
                    {Number(school.inactive_count || 0) > 0 ? (
                      <span className="ml-1 text-xs text-gray-400">
                        / {Number(school.inactive_count)} ngưng
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-300">
                    <ChevronRight className="ml-auto h-5 w-5" />
                  </td>
                </tr>
              ))}
              {!loading && schools.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-gray-500">
                    Không có trường nào phù hợp bộ lọc.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
      />

      <TeachersBySchoolModal
        isOpen={!!selectedSchool}
        school={selectedSchool}
        statusFilter={filters.status}
        schoolLevelFilter={filters.school_level}
        refreshVersion={teacherListVersion}
        onClose={() => setSelectedSchool(null)}
        onEdit={(id) => {
          setEditId(id);
          setShowForm(true);
        }}
        onChanged={refreshAll}
      />

      <TeacherAccountFormModal
        isOpen={showForm}
        employeeId={editId}
        onClose={() => {
          setShowForm(false);
          setEditId(null);
        }}
        onSaved={() => {
          setTeacherListVersion((v) => v + 1);
          refreshAll();
        }}
      />

      <TeacherAccountImportModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onRefresh={refreshAll}
      />
    </div>
  );
};

export default TeacherAccountCardPage;
